import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { AlbumCard, GalleryGrid, videoThumbnail } from '../components/home/GalleryGrid';
import { ApiService } from '../utils/api';
import { formatDate } from '../utils/datetime';
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin, Pause, Play as PlayIcon } from 'lucide-react';

const AUTOPLAY_MS = 5000;
const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Photo slider across every event: auto-advances every few seconds (paused on hover, focus, when the tab is
 * hidden or by the pause button), with arrows, swipe, keyboard and a thumbnail strip. Portrait and landscape
 * photos sit on a blurred copy of themselves so the frame never shows big empty bars.
 */
const PhotoCarousel = ({ photos, albumNames, hasMore, loadMore }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(prefersReducedMotion);
  const [hovered, setHovered] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const touchX = useRef(null);
  const strip = useRef(null);
  const count = photos.length;
  const step = useCallback((delta) => {
    setPlayingId(null);
    setIndex((i) => (i + delta + count) % count);
  }, [count]);

  useEffect(() => {
    if (hasMore && index >= count - 3) loadMore();
  }, [index, count, hasMore, loadMore]);

  useEffect(() => {
    if (paused || hovered || playingId || count < 2) return undefined;
    const timer = setTimeout(() => { if (!document.hidden) step(1); }, AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, paused, hovered, playingId, count, step]);

  useEffect(() => {
    const thumb = strip.current?.children[index];
    if (thumb && strip.current) {
      strip.current.scrollTo({ left: thumb.offsetLeft - strip.current.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
    }
  }, [index]);

  if (count === 0) return null;
  const photo = photos[Math.min(index, count - 1)];
  const isVideo = photo.type === 'VIDEO';
  const src = isVideo ? videoThumbnail(photo) : ApiService.galleryPhotoUrl(photo.id);
  const playing = isVideo && (playingId === photo.id || !src);
  const albumName = albumNames[photo.albumId];

  return (
    <section aria-roledescription="carousel" aria-label="Event photos and videos" className="space-y-3">
      <style>{'@keyframes galleryFade{from{opacity:0}to{opacity:1}}'}</style>
      <div tabIndex={0} role="group" aria-label={`Photo ${index + 1} of ${count}`}
        onKeyDown={(e) => { if (e.key === 'ArrowRight') step(1); if (e.key === 'ArrowLeft') step(-1); }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - (touchX.current ?? e.changedTouches[0].clientX);
          if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
        }}
        className="relative h-[55vh] min-h-[280px] max-h-[540px] overflow-hidden rounded-2xl bg-gray-900 shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
        {src && (
          <img key={`bg-${photo.id}`} src={src} alt="" aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl opacity-60" />
        )}
        {playing ? (
          <div className="relative mx-auto flex h-full max-w-full items-center justify-center px-14 pb-14 pt-4">
            <iframe title={photo.caption || 'Event video'} src={`${photo.embedUrl}${photo.embedUrl.includes('?') ? '&' : '?'}autoplay=${src ? 1 : 0}`}
              className="aspect-video h-full max-h-full w-auto max-w-full rounded-lg bg-black"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
          </div>
        ) : (
          <img key={photo.id} src={src} alt={photo.caption || albumName || 'Event photo'}
            style={{ animation: 'galleryFade 0.6s ease' }}
            className="relative mx-auto h-full w-auto max-w-full object-contain drop-shadow-2xl" />
        )}
        {isVideo && !playing && (
          <button type="button" onClick={() => setPlayingId(photo.id)} aria-label={`Play ${photo.caption || 'video'}`}
            className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-black/60 p-5 hover:bg-red-600 transition-colors"><PlayIcon className="h-10 w-10 text-white fill-white" /></span>
          </button>
        )}
        {count > 1 && (
          <>
            <button type="button" aria-label="Previous photo" onClick={() => step(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-gray-900 shadow hover:bg-white"><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" aria-label="Next photo" onClick={() => step(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-gray-900 shadow hover:bg-white"><ChevronRight className="h-6 w-6" /></button>
          </>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/75 to-transparent px-4 pb-3 pt-12 text-white">
          <div className="min-w-0">
            {photo.caption && <p className="font-medium truncate">{photo.caption}</p>}
            {albumName && <Link to={`/gallery/${photo.albumId}`} className="text-sm text-white/85 hover:underline">{albumName} →</Link>}
          </div>
          <div className="flex shrink-0 items-center gap-2 text-sm text-white/80">
            <span>{index + 1} / {count}{hasMore ? '+' : ''}</span>
            {count > 1 && (
              <button type="button" onClick={() => setPaused((p) => !p)} aria-label={paused ? 'Play slideshow' : 'Pause slideshow'}
                className="rounded-full bg-white/20 p-1.5 hover:bg-white/35">
                {paused ? <PlayIcon className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
      </div>
      {count > 1 && (
        <div ref={strip} className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button key={p.id} type="button" onClick={() => { setPlayingId(null); setIndex(i); }} aria-label={`Show ${p.type === 'VIDEO' ? 'video' : 'photo'} ${i + 1}`} aria-current={i === index}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-md border-2 bg-gray-800 transition ${i === index ? 'border-blue-600' : 'border-transparent opacity-60 hover:opacity-100'}`}>
              {(p.type === 'VIDEO' ? videoThumbnail(p) : ApiService.galleryPhotoUrl(p.id))
                && <img src={p.type === 'VIDEO' ? videoThumbnail(p) : ApiService.galleryPhotoUrl(p.id)} alt="" loading="lazy" className="h-full w-full object-cover" />}
              {p.type === 'VIDEO' && <PlayIcon className="absolute inset-0 m-auto h-5 w-5 text-white fill-white drop-shadow" />}
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

const GalleryPage = () => {
  const { albumId } = useParams();
  const { data: albums = [] } = useQuery({
    queryKey: ['websiteAlbums'],
    queryFn: () => ApiService.getWebsiteAlbums(),
    enabled: !albumId,
    staleTime: 60 * 1000,
  });
  const gallery = useInfiniteQuery({
    queryKey: ['websiteGallery', albumId || ''],
    queryFn: ({ pageParam }) => ApiService.getWebsiteGallery(albumId, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.hasMore ? pages.length : undefined),
    retry: false,
    staleTime: 60 * 1000,
  });
  const pages = gallery.data?.pages || [];
  const album = pages[0]?.album;
  const items = pages.flatMap((page) => page.items);
  const albumNames = Object.fromEntries(albums.map((a) => [a.id, a.name]));
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = gallery;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/"><img src="/logo.png" alt="Home" className="h-14 w-auto" /></Link>
          <Link to="/events"><Button variant="outline">Events</Button></Link>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 space-y-8">
        {albumId ? (
          <div>
            <Link to="/gallery" className="inline-flex items-center text-sm text-blue-600 hover:underline">
              <ArrowLeft className="mr-1 h-4 w-4" /> All albums
            </Link>
            <h1 className="mt-2 text-3xl font-bold text-gray-900">{album?.name || 'Album'}</h1>
            {album && (
              <p className="mt-1 text-gray-600 flex flex-wrap items-center gap-x-4">
                {album.date && <span>{formatDate(album.date)}</span>}
                {album.location && <span className="inline-flex items-center"><MapPin className="mr-1 h-4 w-4" />{album.location}</span>}
              </p>
            )}
          </div>
        ) : (
          <>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gallery</h1>
              <p className="mt-1 text-gray-600">Photos and videos from events on our platform.</p>
            </div>
            <PhotoCarousel photos={items} albumNames={albumNames} hasMore={!!gallery.hasNextPage}
              loadMore={loadMore} />
            {albums.length > 0 && (
              <section>
                <h2 className="mb-4 text-xl font-semibold text-gray-900">Albums</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {albums.map((a) => <AlbumCard key={a.id} album={a} />)}
                </div>
              </section>
            )}
          </>
        )}

        {gallery.isLoading && <p className="text-gray-500">Loading…</p>}
        {gallery.isError && <p className="text-gray-600">{gallery.error.message}. <Link to="/gallery" className="text-blue-600 hover:underline">See all photos</Link></p>}
        {!gallery.isLoading && !gallery.isError && items.length === 0 && <p className="text-gray-500">No photos yet.</p>}
        {albumId && items.length > 0 && <GalleryGrid items={items} />}
        {albumId && gallery.hasNextPage && (
          <div className="text-center">
            <Button variant="outline" onClick={() => gallery.fetchNextPage()} disabled={gallery.isFetchingNextPage}>
              {gallery.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default GalleryPage;
